import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Put Shopify's live recommendations first.
   *
   * The page render already holds a FULL list (curated → brand → fallback collection,
   * see product-recommendations.liquid). This fetches the live recs for the configured
   * intent and moves them to the front; the page's own cards fill the remaining slots.
   * The slides themselves are never added or removed — only which card sits in each —
   * so the carousel's slide count, scroll timeline and arrows stay as rendered.
   *
   * Any failure (offline, aborted, non-200, no live recs) leaves the page render as
   * it is. Nothing here ever hides or empties the section.
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    if (!productId || !this.id || recommendationsPerformed === 'true') return;
    this.dataset.recommendationsPerformed = 'true';

    let live = [];
    try {
      const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
      const response = await fetch(url);
      if (!response.ok) return;
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const fetched = html.querySelector(`product-recommendations[id="${this.id}"]`);
      live = fetched ? [...fetched.querySelectorAll('.resource-list__item')].filter(productIdOf) : [];
    } catch {
      return;
    }
    if (!live.length) return;

    const slots = [...this.querySelectorAll('.resource-list__item')];
    const liveIds = new Set(live.map(productIdOf));
    const ordered = [...live, ...slots.filter((slot) => !liveIds.has(productIdOf(slot)))]
      .slice(0, slots.length)
      .map((item) => item.outerHTML);

    requestAnimationFrame(() => {
      slots.forEach((slot, i) => {
        if (slot.outerHTML !== ordered[i]) slot.outerHTML = ordered[i];
      });
    });
  }
}

/**
 * @param {Element} item a .resource-list__item
 * @returns {string | undefined}
 */
function productIdOf(item) {
  return item.querySelector('[data-product-id]')?.getAttribute('data-product-id') ?? undefined;
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
